/**
 * Workaround for a race in Boost AI Search & Discovery's Instant Search widget
 * (widget-integration 1.88.2).
 *
 * Boost renders its widget template only after the first user interaction, so on
 * that first click `openSuggestion()` runs before the results wrapper exists:
 *
 *   let t = getSuggestionWrapper();                       // -> null on first click
 *   t && requestAnimationFrame(() => t.classList.remove('boost-sd__g-hide'));
 *   ...
 *   this.state.isOpen = !0;                               // set anyway
 *
 * The un-hide is skipped but `isOpen` is still set, and every later focus/click
 * then returns early on `openSuggestion`'s first guard:
 *
 *   if (this.isClosing || (this.state.isOpen && this.state.activeInputId === e.id)) return;
 *
 * so the dropdown stays hidden for the life of the page. Boost's CSS also hides the
 * theme's own predictive-search panel while its body classes are set, leaving the
 * page dimmed with no suggestions at all.
 *
 * This detects that state (isOpen true, wrapper still carrying boost-sd__g-hide) and
 * replays Boost's own open path, which is what Escape + a second click does manually.
 * Remove this file once Boost ships a fix.
 */
(function () {
  const HIDE_CLASS = 'boost-sd__g-hide';
  const WRAPPER_SEL =
    '.boost-sd__instant-search-container-desktop > div,'
    + '.boost-sd__instant-search-container-mobile > div';
  const RETRY_DELAYS = [60, 250, 600, 1200];

  function getController() {
    return window.boostISWModule && window.boostISWModule.instantSearchController;
  }

  // Boost keeps a desktop and a mobile wrapper; only one container is laid out.
  function getLiveWrapper() {
    const wrappers = document.querySelectorAll(WRAPPER_SEL);
    for (const wrapper of wrappers) {
      const container = wrapper.parentElement;
      if (container && getComputedStyle(container).display !== 'none') return wrapper;
    }
    return null;
  }

  function isDeadlocked(controller, wrapper) {
    return !!controller
      && !!controller.state
      && controller.state.isOpen
      && !controller.isClosing
      && !!wrapper
      && wrapper.classList.contains(HIDE_CLASS);
  }

  function unstick() {
    const controller = getController();
    const wrapper = getLiveWrapper();
    if (!isDeadlocked(controller, wrapper)) return;

    const input = controller.state.activeInputId
      && document.getElementById(controller.state.activeInputId);
    if (!input) return;

    // Clearing isOpen lets openSuggestion past its guard so Boost positions the
    // panel, sets aria state and arms its focus trap itself.
    controller.state.isOpen = false;
    try {
      Promise.resolve(controller.openSuggestion(input)).catch(() => {});
    } catch (e) {
      // ignore
    }

    // If Boost still leaves it hidden, unhide directly rather than showing nothing.
    setTimeout(() => {
      const current = getLiveWrapper();
      const ctrl = getController();
      if (current && ctrl && ctrl.state.isOpen && current.classList.contains(HIDE_CLASS)) {
        current.classList.remove(HIDE_CLASS);
      }
    }, 150);
  }

  function isSearchInput(el) {
    return !!el
      && el.tagName === 'INPUT'
      && (el.name === 'q'
        || el.classList.contains('search__input')
        || el.classList.contains('boost-sd__search-widget-init-input'));
  }

  ['focusin', 'click', 'input'].forEach((eventName) => {
    document.addEventListener(eventName, (evt) => {
      if (!isSearchInput(evt.target)) return;
      RETRY_DELAYS.forEach((delay) => setTimeout(unstick, delay));
    }, true);
  });
})();
